/**
 * Idea Snapshots API Hooks
 *
 * React Query hooks for listing and rolling back idea snapshots.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/query-client';
import apiClient from '../../services/unifiedApiClient';

/**
 * Fetch snapshot history for an idea.
 *
 * @param {string} ideaId
 * @returns Query with snapshot list data
 */
export function useIdeaSnapshots(ideaId) {
  return useQuery({
    queryKey: [...queryKeys.ideas.detail(ideaId), 'snapshots'],
    queryFn: async () => {
      return await apiClient.get(`/ideas/${ideaId}/snapshots`);
    },
    enabled: !!ideaId,
  });
}

/**
 * Roll back an idea to a specific snapshot.
 *
 * @returns Mutation that takes { ideaId, snapshotId }
 */
export function useRollbackSnapshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ideaId, snapshotId }) => {
      return await apiClient.post(
        `/ideas/${ideaId}/snapshots/${snapshotId}/rollback`
      );
    },
    onSuccess: (data, { ideaId }) => {
      // Invalidate idea to refresh spec + version
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.lists() });
      // Invalidate plans since they may have changed
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.all });
      // Invalidate snapshots list (a pre_rollback snapshot was just created)
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.ideas.detail(ideaId), 'snapshots'],
      });
    },
    onError: (error) => {
      console.error('Failed to rollback snapshot:', error);
    },
  });
}
